import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement | undefined} cartBubbleAccessibleCount - The accessible cart count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  // Keep the three original Horizon refs mandatory. The accessible-count ref
  // was added by GVS, but treating it as mandatory can prevent the entire cart
  // icon from initialising when cached or partially deployed snippet markup is
  // momentarily older than this asset.
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const countText =
      this.refs.cartBubbleAccessibleCount?.textContent ?? this.refs.cartBubbleCount?.textContent ?? '0';
    const count = Number.parseInt(countText, 10);

    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';

    if (this.refs.cartBubbleAccessibleCount instanceof HTMLElement) {
      this.refs.cartBubbleAccessibleCount.textContent = String(value);
    } else {
      // Compatibility fallback for a cart-bubble snippet rendered before the
      // dedicated accessible-count ref was introduced. Updating the existing
      // visually-hidden text keeps screen-reader output accurate without making
      // the visible basket badge depend on that newer ref.
      const accessibleLabel = this.querySelector('.cart-bubble__text > .visually-hidden');
      if (accessibleLabel instanceof HTMLElement) {
        accessibleLabel.textContent = (accessibleLabel.textContent ?? '').replace(
          /(:\s*)\d+\s*$/,
          `$1${value}`
        );
      }
    }

    const trigger = this.closest('[data-cart-label]');
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-label', `${trigger.dataset.cartLabel}: ${value}`);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const sectionCount = this.#getSectionCartCount(event.detail.data?.sections);

    if (sectionCount !== null) {
      // Product-form responses can complete out of order when customers add
      // quickly. An add must never reduce a newer, already-rendered count.
      const authoritativeCount = comingFromProductForm
        ? Math.max(this.currentCartCount, sectionCount)
        : sectionCount;

      this.renderCartBubble(authoritativeCount, false);
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Reads the server-rendered cart count returned by Shopify section rendering.
   * This is more reliable than incrementing the browser's previous value when
   * inventory limits, bundled additions or rapid requests alter the result.
   * @param {Record<string, string> | undefined} sections
   * @returns {number | null}
   */
  #getSectionCartCount(sections) {
    if (!sections) return null;

    for (const sectionHtml of Object.values(sections)) {
      if (typeof sectionHtml !== 'string') continue;

      const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const countText = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      if (!countText) continue;

      const count = Number.parseInt(countText, 10);
      if (Number.isFinite(count) && count >= 0) return count;
    }

    return null;
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const parsedItemCount = Number(itemCount);
    if (!Number.isFinite(parsedItemCount) || parsedItemCount < 0) return;

    const nextCount = comingFromProductForm ? this.currentCartCount + parsedItemCount : parsedItemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const renderedCount = String(this.currentCartCount);

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === renderedCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
